import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"
import { ButtonPrimary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import {
    useCurrencyPolicy,
    useDeleteCurrencyPolicy,
    useUpdateCurrencyPolicy,
} from "features/pilot-tracking/currency/CurrencyPolicyQueries"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { CurrencyPolicyForm } from "./CurrencyPolicyForm"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    loadPolicy,
    selectFormAsCreatable,
    selectSubmitting,
    setSubmitting,
} from "features/pilot-tracking/currency/slice"
import { Dropdown } from "@nova/component-dropdown"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"

function BackButton() {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <IconButton
            onClick={() => router.navigate({ path: "/fleet/pilots/currency" })}
            className="pr-2"
        >
            <Icons.SmallIcon Icon={Icons.ArrowLeft} />
        </IconButton>
    )
}

function DeleteButton() {
    const { policyId } = useParams<{ policyId: string }>()
    const { data } = useCurrencyPolicy(policyId!)
    const { deleteCurrencyPolicy } = useDeleteCurrencyPolicy()
    const confirmationModal = useConfirmationModalContext()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")

    const policyName = data?.name ?? "this policy"

    const handleDelete = () => {
        confirmationModal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: `Delete ${policyName}?`,
                message: `Are you sure you want to delete ${policyName}?`,
                confirmText: "Delete",
            }),
            asyncConfirmCallback: async () => {
                try {
                    await deleteCurrencyPolicy(policyId!)
                    dispatch(
                        setGlobalNotificationContext({
                            message: `${policyName} has been deleted.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 5000,
                        })
                    )
                    router.navigate({ path: "/fleet/pilots/currency" })
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Failed to delete ${policyName}. Please try again.`,
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        })
    }

    return (
        <Dropdown>
            <Dropdown.TriggerButton className="border border-gray-700" withoutDropdownArrow>
                <Icons.SmallIcon Icon={Icons.MoreVert} className="px-3" />
            </Dropdown.TriggerButton>
            <Dropdown.Menu>
                <Dropdown.ButtonItem className="gap-2 !text-red-500" onClick={handleDelete}>
                    <Icons.SmallIcon Icon={Icons.Trash} />
                    Delete
                </Dropdown.ButtonItem>
            </Dropdown.Menu>
        </Dropdown>
    )
}

function UpdatePolicyButton() {
    const { policyId } = useParams<{ policyId: string }>()
    const form = useAppSelector(selectFormAsCreatable)
    const { isLoading } = useCurrencyPolicy(policyId!)
    const { updateCurrencyPolicy } = useUpdateCurrencyPolicy()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)

    const handleSubmit = useCallback(async () => {
        dispatch(setSubmitting(true))
        try {
            await updateCurrencyPolicy({ policyId: policyId!, body: form })
            dispatch(
                setGlobalNotificationContext({
                    message: `${form.name} has been saved.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            router.navigate({ path: "/fleet/pilots/currency" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to save ${form.name}. Please try again.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setSubmitting(false))
        }
    }, [form, policyId, updateCurrencyPolicy, dispatch, router])

    return (
        <ButtonPrimary
            title="Publish"
            RightIcon={Icons.Upload}
            onClick={handleSubmit}
            disabled={isLoading || submitting}
        />
    )
}

export const EditCurrencyPolicyPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Edit Policy",
        leftItems: [BackButton],
        rightItems: [DeleteButton, UpdatePolicyButton],
    }),
    Content: EditCurrencyPolicyPageContent,
    maxWidth: 1200,
})

function EditCurrencyPolicyPageContent() {
    const { policyId } = useParams<{ policyId: string }>()
    const { data, isLoading } = useCurrencyPolicy(policyId!)
    const dispatch = useAppDispatch()
    const [isFormReady, setIsFormReady] = useState(false)

    useEffect(() => {
        if (data && !isFormReady) {
            const { users, recipients, ...policy } = data
            dispatch(loadPolicy({ policy, users, recipients }))
            setIsFormReady(true)
        }
    }, [data, isFormReady, dispatch])

    if (isLoading || !isFormReady) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    return <CurrencyPolicyForm />
}
